/**
 * Admin Feature Flags page — /admin/settings/features
 *
 * Backed by the existing infrastructure in /app/backend/feature_flag_service.py
 * and /app/backend/routers/feature_flags.py.
 *
 * Surface:
 *   - 3 preset buttons (Launch / Growth / Full) + Refresh
 *   - 4 stat tiles (total / enabled / internal / disabled)
 *   - Per-category cards (Core, Customer, Partner, Analytics)
 *   - Per-flag row: name + description + 3-state segmented selector + dependency
 *     pill ("requires Memberships") + last-modified info + audit-log drawer
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, EyeOff, Lock, RefreshCw, History, AlertTriangle, X, Sparkles } from "lucide-react";
import { PortalShell } from "@/components/PortalShell";
import { Button } from "@/components/ui/button";
import { ReviewReferralRolloutPresets } from "@/components/admin/ReviewReferralRolloutPresets";
import api from "@/lib/api";
import { formatDateTimeAU } from "@/lib/formatDateAU";

const STATUSES = [
  { v: "enabled",       l: "Enabled",       Icon: ShieldCheck, color: "bg-emerald-500", ring: "ring-emerald-500", desc: "Visible to all users." },
  { v: "internal_only", l: "Internal Only", Icon: EyeOff,      color: "bg-amber-500",   ring: "ring-amber-500",   desc: "Visible to admin + lab staff only." },
  { v: "disabled",      l: "Disabled",      Icon: Lock,        color: "bg-rose-500",    ring: "ring-rose-500",    desc: "Hidden everywhere; API returns 403." },
];

const CATEGORY_META = {
  core:      { label: "Core Operations",     hint: "Booking, payments, lab workflow, reports.",        order: 1 },
  customer:  { label: "Customer Features",   hint: "AWARE Water Health Record, memberships, transfers.", order: 2 },
  partner:   { label: "Partner Features",    hint: "Trade portal, branding, referrals, commissions.", order: 3 },
  analytics: { label: "Advanced Analytics",  hint: "Internal dashboards driven by partner data.",     order: 4 },
};

const PRESETS = [
  { mode: "launch", title: "Aware Launch Mode",     blurb: "Core operational features only. All partner + conveyancing tooling stays internal." },
  { mode: "growth", title: "Growth Mode",          blurb: "Launch + conveyancing certificates + partner portal + property sharing." },
  { mode: "full",   title: "Full Platform Mode",   blurb: "Everything enabled — final phase of the Aware roadmap." },
];


export default function FeatureFlagsPage() {
  const [flags, setFlags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState(null);
  const [busyPreset, setBusyPreset] = useState(null);
  const [auditFor, setAuditFor] = useState(null);
  const [audit, setAudit] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/feature-flags");
      setFlags(data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load feature flags");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const flagsByKey = useMemo(() => Object.fromEntries(flags.map((f) => [f.key, f])), [flags]);
  const counts = useMemo(() => ({
    total: flags.length,
    enabled: flags.filter((f) => f.status === "enabled").length,
    internal: flags.filter((f) => f.status === "internal_only").length,
    disabled: flags.filter((f) => f.status === "disabled").length,
  }), [flags]);

  const grouped = useMemo(() => {
    const groups = {};
    for (const f of flags) {
      const cat = f.category || "core";
      (groups[cat] ||= []).push(f);
    }
    return Object.entries(groups).sort(
      ([a], [b]) => (CATEGORY_META[a]?.order ?? 99) - (CATEGORY_META[b]?.order ?? 99)
    );
  }, [flags]);

  const updateStatus = async (key, status, override = false) => {
    setBusyKey(key);
    try {
      await api.put(`/admin/feature-flags/${key}`, { status, reason: "admin UI update", override_dependency: override });
      toast.success(`Updated ${flagsByKey[key]?.name || key} → ${status}`);
      await load();
    } catch (e) {
      const detail = e.response?.data?.detail || "Update failed";
      if (typeof detail === "string" && detail.startsWith("dependency_disabled:")) {
        const dep = detail.split(":")[1];
        const ok = window.confirm(`This feature depends on "${dep}" which is currently disabled. Enable anyway?`);
        if (ok) return updateStatus(key, status, true);
      } else {
        toast.error(detail);
      }
    } finally {
      setBusyKey(null);
    }
  };

  const applyPreset = async (mode) => {
    const ok = window.confirm(`Apply "${mode}" preset? This will update multiple feature flags.`);
    if (!ok) return;
    setBusyPreset(mode);
    try {
      const { data } = await api.post("/admin/feature-flags/preset", { mode, reason: `Admin UI preset (${mode})` });
      toast.success(`Preset applied — ${data.total_changed} flag(s) changed`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Preset failed");
    } finally {
      setBusyPreset(null);
    }
  };

  const openAudit = async (flag) => {
    setAuditFor(flag);
    try {
      const { data } = await api.get(`/admin/feature-flags/${flag.key}/audit`);
      setAudit(data || []);
    } catch (e) {
      setAudit([]);
      toast.error(e.response?.data?.detail || "Could not load audit log");
    }
  };

  return (
    <PortalShell title="Platform Features" subtitle="Selectively enable or disable major capabilities without removing code.">
      {/* Presets */}
      <section data-testid="features-presets" className="rounded-3xl bg-gradient-to-br from-slate-900 via-slate-800 to-cyan-900 text-white p-6 lg:p-8">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-cyan-300 flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5" /> Quick Presets
            </div>
            <h2 className="font-display text-xl lg:text-2xl font-bold mt-1.5">One-click platform configuration</h2>
            <p className="mt-1.5 text-sm text-slate-300 max-w-xl">
              Move the entire platform between launch, growth and full modes. Per-flag overrides remain available after applying a preset.
            </p>
          </div>
          <Button onClick={load} variant="outline"
            data-testid="features-refresh-btn"
            className="rounded-full bg-white/5 border-white/20 text-white hover:bg-white/10">
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
          </Button>
        </div>

        <div className="mt-5 grid md:grid-cols-3 gap-3">
          {PRESETS.map((p) => (
            <div key={p.mode} className="rounded-2xl bg-white/5 border border-white/10 p-4">
              <div className="font-semibold text-cyan-200">{p.title}</div>
              <p className="mt-1 text-xs text-slate-300 leading-relaxed h-12">{p.blurb}</p>
              <Button
                onClick={() => applyPreset(p.mode)} disabled={busyPreset === p.mode}
                data-testid={`features-preset-${p.mode}`}
                className="mt-3 w-full rounded-full bg-cyan-500 hover:bg-cyan-400 text-slate-900 font-semibold"
              >
                {busyPreset === p.mode ? "Applying…" : "Apply"}
              </Button>
            </div>
          ))}
        </div>
      </section>

      {/* Stats */}
      <section className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Total flags" value={counts.total} testid="features-stat-total" />
        <Stat label="Enabled" value={counts.enabled} accent="text-emerald-600" testid="features-stat-enabled" />
        <Stat label="Internal Only" value={counts.internal} accent="text-amber-600" testid="features-stat-internal" />
        <Stat label="Disabled" value={counts.disabled} accent="text-rose-600" testid="features-stat-disabled" />
      </section>

      {/* Review Follow-up rollout presets — Phase 2L-A83 */}
      <ReviewReferralRolloutPresets onApplied={load} />

      {/* Review Follow-up smoke tools — Phase 2L-A79d/A80 */}
      <ReviewFollowupSmokeTools />

      {/* Flag categories */}
      {loading ? (
        <div className="mt-8 text-sm text-slate-500">Loading feature flags…</div>
      ) : (
        <div className="mt-6 space-y-6">
          {grouped.map(([cat, list]) => {
            const meta = CATEGORY_META[cat] || { label: cat, hint: "" };
            return (
              <section key={cat} data-testid={`features-category-${cat}`} className="rounded-3xl bg-white border border-slate-200 overflow-hidden">
                <header className="px-6 py-4 border-b border-slate-100 bg-slate-50/60 flex items-baseline justify-between gap-3">
                  <div>
                    <h3 className="font-display text-lg font-bold text-slate-900">{meta.label}</h3>
                    {meta.hint && <p className="text-xs text-slate-500 mt-0.5">{meta.hint}</p>}
                  </div>
                  <div className="text-xs text-slate-500">{list.length} feature{list.length === 1 ? "" : "s"}</div>
                </header>
                <ul className="divide-y divide-slate-100">
                  {list.map((f) => (
                    <FlagRow
                      key={f.key} flag={f}
                      busy={busyKey === f.key}
                      onChange={(s) => updateStatus(f.key, s)}
                      onAudit={() => openAudit(f)}
                      flagsByKey={flagsByKey}
                    />
                  ))}
                </ul>
              </section>
            );
          })}
        </div>
      )}

      {auditFor && (
        <AuditDrawer
          flag={auditFor} entries={audit}
          onClose={() => { setAuditFor(null); setAudit([]); }}
        />
      )}
    </PortalShell>
  );
}


function Stat({ label, value, accent = "text-slate-900", testid }) {
  return (
    <div data-testid={testid} className="rounded-2xl bg-white border border-slate-200 p-4">
      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{label}</div>
      <div className={`font-display text-3xl font-bold mt-1 ${accent} tabular-nums`}>{value}</div>
    </div>
  );
}


function FlagRow({ flag, busy, onChange, onAudit, flagsByKey }) {
  const depWarnings = (flag.dependencies || [])
    .map((d) => {
      const dep = flagsByKey[d];
      if (!dep) return null;
      if (dep.status === "disabled") return { key: d, label: dep.name, severity: "block" };
      return null;
    })
    .filter(Boolean);

  return (
    <li className="px-6 py-4 flex flex-col lg:flex-row lg:items-center gap-4" data-testid={`feature-row-${flag.key}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="font-semibold text-slate-900">{flag.name}</div>
          <code className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 font-mono">{flag.key}</code>
        </div>
        <p className="mt-1 text-xs text-slate-600 leading-relaxed">{flag.description}</p>
        <div className="mt-1.5 flex items-center gap-3 flex-wrap text-[11px] text-slate-500">
          {flag.last_modified_at && (
            <span>Modified {formatDateTimeAU(flag.last_modified_at)}{flag.last_modified_by ? ` · ${flag.last_modified_by}` : ""}</span>
          )}
          {flag.dependencies?.length > 0 && (
            <span className="text-slate-400">Requires: {flag.dependencies.join(", ")}</span>
          )}
        </div>
        {depWarnings.length > 0 && flag.status !== "disabled" && (
          <div className="mt-2 inline-flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-full bg-amber-50 text-amber-800 border border-amber-200">
            <AlertTriangle className="w-3 h-3" />
            Depends on disabled flag: {depWarnings.map((d) => d.label).join(", ")}
          </div>
        )}
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <SegmentedSelector value={flag.status} onChange={onChange} disabled={busy} testIdPrefix={`feature-status-${flag.key}`} />
        <button
          onClick={onAudit}
          data-testid={`feature-audit-${flag.key}`}
          className="w-9 h-9 inline-flex items-center justify-center rounded-lg text-slate-600 hover:bg-slate-100"
          title="View audit log"
        >
          <History className="w-4 h-4" />
        </button>
      </div>
    </li>
  );
}


function SegmentedSelector({ value, onChange, disabled, testIdPrefix }) {
  return (
    <div className="inline-flex rounded-full bg-slate-100 p-1" role="radiogroup">
      {STATUSES.map(({ v, l, Icon, color }) => {
        const active = value === v;
        return (
          <button
            key={v} disabled={disabled}
            data-testid={`${testIdPrefix}-${v}`}
            onClick={() => !active && onChange(v)}
            title={l}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold transition-colors ${
              active ? `${color} text-white shadow-sm` : "text-slate-600 hover:text-slate-900"
            } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
          >
            <Icon className="w-3 h-3" /> <span className="hidden sm:inline">{l}</span>
          </button>
        );
      })}
    </div>
  );
}


function AuditDrawer({ flag, entries, onClose }) {
  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={onClose} />
      <div className="absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl flex flex-col" data-testid="features-audit-drawer">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Audit log</div>
            <h3 className="font-display text-base font-bold text-slate-900 mt-0.5">{flag.name}</h3>
          </div>
          <button onClick={onClose} data-testid="features-audit-close" className="w-9 h-9 inline-flex items-center justify-center rounded-lg text-slate-600 hover:bg-slate-100">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {entries.length === 0 ? (
            <p className="text-sm text-slate-500">No audit entries yet.</p>
          ) : (
            <ol className="space-y-3">
              {entries.slice().reverse().map((e, i) => (
                <li key={i} className="rounded-xl bg-slate-50 border border-slate-100 p-3">
                  <div className="text-xs text-slate-500">{formatDateTimeAU(e.at)} · {e.actor || "system"}</div>
                  <div className="mt-1 text-sm font-semibold text-slate-900">
                    {e.event === "seeded" ? (
                      <>Seeded as <code className="font-mono text-cyan-700">{e.new}</code></>
                    ) : (
                      <>
                        <code className="font-mono text-rose-600">{e.old || "—"}</code>
                        {" → "}
                        <code className="font-mono text-emerald-600">{e.new}</code>
                      </>
                    )}
                  </div>
                  {e.reason && <div className="mt-1 text-xs text-slate-600">{e.reason}</div>}
                  {e.override_dependency && (
                    <div className="mt-1 text-[10px] uppercase tracking-wider font-bold text-amber-700">Dependency override</div>
                  )}
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>
    </div>
  );
}


/**
 * Phase 2L-A79d/A80 — admin tools for smoke-testing the review
 * follow-up + invite-a-neighbour + voucher loop without waiting for a
 * real 24-hour follow-up.
 *
 * Hits POST /api/admin/review-followup/create-smoke-token via the
 * existing authenticated `api` client (carries the admin JWT
 * automatically — no manual token extraction).
 */
function ReviewFollowupSmokeTools() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // { referral_token, invite_url, expires_at, is_smoke_test }
  const [err, setErr] = useState("");

  const createToken = async () => {
    setBusy(true); setErr(""); setResult(null);
    try {
      const { data } = await api.post("/admin/review-followup/create-smoke-token");
      setResult(data);
      toast.success("Smoke referral token created");
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Failed to create smoke token";
      setErr(typeof msg === "string" ? msg : JSON.stringify(msg));
      toast.error("Could not create smoke token");
    } finally { setBusy(false); }
  };

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch {
      toast.error("Clipboard unavailable");
    }
  };

  return (
    <section
      data-testid="smoke-tools-section"
      className="mt-6 rounded-3xl border border-amber-200 bg-amber-50/60 p-6"
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-[10px] tracking-[0.25em] font-bold uppercase text-amber-700 mb-1">
            Review Follow-up · smoke testing
          </div>
          <h2 className="text-xl font-bold text-amber-900">Create smoke referral token</h2>
          <p className="mt-1 text-sm text-amber-900/80 leading-relaxed max-w-2xl">
            Generates a synthetic <code className="font-mono text-xs bg-amber-100 px-1.5 py-0.5 rounded">review_followups</code> row
            marked <code className="font-mono text-xs bg-amber-100 px-1.5 py-0.5 rounded">is_smoke_test:true</code> with a real
            referral token, so you can exercise <code className="font-mono text-xs bg-amber-100 px-1.5 py-0.5 rounded">/invite-a-neighbour</code>
            and the voucher flow end-to-end. Smoke rows auto-expire in 24h, are excluded from /recent-tokens, and the scheduler
            never sends emails for them. No customer data is touched.
          </p>
        </div>
        <Button
          data-testid="smoke-create-token-btn"
          onClick={createToken}
          disabled={busy}
          className="shrink-0 rounded-full bg-amber-600 hover:bg-amber-700 text-white font-semibold"
        >
          {busy ? "Creating…" : "Create smoke referral token"}
        </Button>
      </div>

      {err && (
        <div
          data-testid="smoke-create-error"
          className="mt-4 rounded-xl bg-rose-50 border border-rose-200 text-rose-800 text-sm px-4 py-3"
        >
          {err}
        </div>
      )}

      {result && (
        <div
          data-testid="smoke-create-result"
          className="mt-4 rounded-2xl bg-white border border-amber-200 p-4 space-y-3"
        >
          <Row
            label="Invite URL"
            value={result.invite_url}
            onCopy={() => copy(result.invite_url, "Invite URL")}
            testid="smoke-invite-url"
          />
          <Row
            label="Referral token"
            value={result.referral_token}
            onCopy={() => copy(result.referral_token, "Referral token")}
            testid="smoke-referral-token"
            mono
          />
          <div className="flex items-center justify-between text-xs text-slate-600">
            <span>
              Expires <strong>{result.expires_at ? new Date(result.expires_at).toLocaleString() : "—"}</strong>
            </span>
            <span className="text-[10px] tracking-[0.18em] font-bold uppercase text-amber-700">
              is_smoke_test · auto-cleaned by TTL
            </span>
          </div>
          <p className="text-xs text-slate-600 leading-relaxed">
            Open the invite URL to test the form. From there, send an invite to your own inbox to generate a voucher.
            Use that invite link to test <code className="font-mono text-[11px] bg-slate-100 px-1 rounded">/book?invite=…</code>
            with the <code className="font-mono text-[11px] bg-slate-100 px-1 rounded">voucher_redemption_booking</code> flag flipped on.
          </p>
        </div>
      )}
    </section>
  );
}

function Row({ label, value, onCopy, testid, mono }) {
  return (
    <div>
      <div className="text-[10px] tracking-[0.2em] font-bold uppercase text-slate-500 mb-1">{label}</div>
      <div className="flex items-center gap-2">
        <code
          data-testid={testid}
          className={`flex-1 min-w-0 truncate rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-xs text-slate-900 ${mono ? "font-mono" : ""}`}
          title={value}
        >
          {value}
        </code>
        <Button
          variant="outline"
          onClick={onCopy}
          data-testid={`${testid}-copy`}
          className="rounded-lg text-xs"
        >
          Copy
        </Button>
      </div>
    </div>
  );
}
